import {
    POST_ACCESS_ITEM_SCOPE,
} from '../enums';
import type {
    IPostAccessItem,
    IReadPostItem,
} from '../interfaces';

function hasFullPostAccess(data: {
    postAccessItem: IPostAccessItem | null,
    nowTimestamp: number,
}): boolean {
    return (
        !!data.postAccessItem
        && data.postAccessItem.scope === POST_ACCESS_ITEM_SCOPE.full
        && (
            data.postAccessItem.expires == null
            || data.postAccessItem.expires > data.nowTimestamp
        )
    );
}

function hasLimitedScopeAccess(data: {
    limitedAccessItems: Map<string, number> | null,
    scopeIds: string[],
    nowTimestamp: number,
}): boolean {
    return data.scopeIds.some((scopeId) => {
        const accessExpiry = data.limitedAccessItems?.get(scopeId);
        return accessExpiry === Infinity || (
            accessExpiry != null
            && accessExpiry > data.nowTimestamp
        );
    });
}

/**
 * The contents table needs a deterministic access answer per scope so it can
 * render lock state without waiting for the reader body to fetch a deeper
 * section. These helpers mirror the current reader paywall/auth-lock policy.
 */
export function hasReaderOutlineChapterAccess(data: {
    post: IReadPostItem | null | undefined,
    chapterIndex: number,
    postAccessItem: IPostAccessItem | null,
    limitedAccessItems: Map<string, number> | null,
    userIsAdmin: boolean,
    nowTimestamp: number,
}): boolean {
    const chapter = data.post?.chapters?.[data.chapterIndex];
    if (!data.post || !chapter) {
        return false;
    }

    return (
        (
            !data.post.paywalled
            && chapter.paywalled != null
            && !chapter.paywalled
        )
        || (
            data.post.paywalled
            && chapter.paywalled != null
            && !chapter.paywalled
        )
        || (
            (
                data.post.paywalled
                || (
                    chapter.paywalled != null
                    && chapter.paywalled
                )
            )
            && (
                hasFullPostAccess({
                    postAccessItem: data.postAccessItem,
                    nowTimestamp: data.nowTimestamp,
                })
                || (
                    data.postAccessItem?.scope === POST_ACCESS_ITEM_SCOPE.limited
                    && hasLimitedScopeAccess({
                        limitedAccessItems: data.limitedAccessItems,
                        scopeIds: [chapter.id],
                        nowTimestamp: data.nowTimestamp,
                    })
                )
            )
        )
        || data.userIsAdmin
    );
}

/**
 * Section rows inherit chapter/post unlocks, but they also honor limited access
 * grants scoped to either the whole chapter or the individual section.
 */
export function hasReaderOutlineSectionAccess(data: {
    post: IReadPostItem | null | undefined,
    chapterIndex: number,
    sectionIndex: number,
    postAccessItem: IPostAccessItem | null,
    limitedAccessItems: Map<string, number> | null,
    userIsAdmin: boolean,
    nowTimestamp: number,
}): boolean {
    const chapter = data.post?.chapters?.[data.chapterIndex];
    const section = chapter?.sections?.[data.sectionIndex];
    if (!data.post || !chapter || !section) {
        return false;
    }

    return (
        (
            !data.post.paywalled
            && chapter.paywalled != null
            && !chapter.paywalled
            && section.paywalled != null
            && !section.paywalled
        )
        || (
            data.post.paywalled
            && chapter.paywalled != null
            && !chapter.paywalled
        )
        || (
            data.post.paywalled
            && chapter.paywalled != null
            && chapter.paywalled
            && section.paywalled != null
            && !section.paywalled
        )
        || (
            (
                data.post.paywalled
                || (
                    chapter.paywalled != null
                    && chapter.paywalled
                )
                || (
                    section.paywalled != null
                    && section.paywalled
                )
            )
            && (
                hasFullPostAccess({
                    postAccessItem: data.postAccessItem,
                    nowTimestamp: data.nowTimestamp,
                })
                || (
                    data.postAccessItem?.scope === POST_ACCESS_ITEM_SCOPE.limited
                    && hasLimitedScopeAccess({
                        limitedAccessItems: data.limitedAccessItems,
                        scopeIds: [chapter.id, section.id],
                        nowTimestamp: data.nowTimestamp,
                    })
                )
            )
        )
        || data.userIsAdmin
    );
}
